import React, {useEffect, useRef, useState} from 'react'
import {shallowEqual} from 'react-redux'
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material'
import {useApplicationServices} from '../backend/ApplicationServicesContext'
import {useAppDispatch, useAppSelector} from '../store/hooks'
import {SET_SESSION_VIEW, UPDATE_TIMER} from '../store/scheduleDuck'
import type {Schedule} from '../types/domain'
import type {HostCredential, PublishSessionRequest, SessionContact} from './domain/sessionProtocol'
import {HOST_HEARTBEAT_MS} from './domain/sessionProtocol'
import type {SessionSnapshot, ServerTimeAnchor, TimerSnapshot} from './domain/timerSnapshot'
import {anchorServerTime, projectTimer, restoreTimer, serverTimeAt} from './domain/timerSnapshot'
import {reconcileLiveTimer} from './domain/reconcileLiveTimer'
import {isRetryableSessionError} from './domain/sessionErrors'
import {scheduleFromState, skipSnapshot, snapshotFromState, stateFromSnapshot} from './timerState'
import {SessionContext} from './SessionContext'
import type {SessionControls, TimerSegment} from './SessionContext'
import {acquireTabOwnership} from './tabOwnership'
import playAlert from '../audibleAlert'
import {viewerRefreshIntervalMs} from './viewerRefreshInterval'
import {DEFAULT_SESSION_VIEW} from './sessionViewState'
import type {SessionViewState} from './sessionViewState'

type Status = SessionControls['status']
interface PendingPublication {request: PublishSessionRequest; revision: number}
interface SavedHost {credential: HostCredential; sequence: number}

export function SessionCoordinator({uid, children}: {uid: string | null; children: React.ReactNode}) {
  const {sessionService: service} = useApplicationServices()
  const state = useAppSelector(value => ({...value}), shallowEqual)
  const dispatch = useAppDispatch()
  const stateRef = useRef(state)
  stateRef.current = state
  const view = state.sessionView ?? DEFAULT_SESSION_VIEW
  const {status, session, availableAtMs, syncStatus, error, busy, serverNowMs: now} = view
  const updateView = (payload: Partial<SessionViewState>) => dispatch({type: SET_SESSION_VIEW, payload})
  const setStatus = (value: Status) => updateView({status: value})
  const setSession = (value: SessionSnapshot | null) => updateView({session: value})
  const setAvailableAt = (value: number | null) => updateView({availableAtMs: value})
  const setSyncStatus = (value: SessionControls['syncStatus']) => updateView({syncStatus: value})
  const setError = (value: string | null) => updateView({error: value})
  const setBusy = (value: boolean) => updateView({busy: value})
  const setNow = (value: number) => updateView({serverNowMs: value})
  const statusRef = useRef(status)
  const changeStatus = (value: Status) => { statusRef.current = value; setStatus(value) }
  const sessionRef = useRef(session)
  const clock = useRef<ServerTimeAnchor>({serverAtReceiptMs: Date.now(), monotonicAtReceiptMs: performance.now()})
  const serverNow = () => serverTimeAt(clock.current, performance.now())
  const host = useRef<SavedHost | null>(null)
  const releaseLock = useRef<(() => void) | null>(null)
  const pending = useRef<PendingPublication | null>(null)
  const acknowledgedRevision = useRef<number | null>(null)
  const inFlight = useRef<number | null>(null)
  const hostWorkEpoch = useRef(0)
  const recoveryInFlight = useRef<number | null>(null)
  const viewRefreshInFlight = useRef<number | null>(null)
  const nextViewerRead = useRef(Infinity)
  const viewerRetryAt = useRef(0)
  const refreshInterval = viewerRefreshIntervalMs(import.meta.env.VITE_SESSION_REFRESH_INTERVAL_SECONDS)
  const resetHostWork = () => {
    hostWorkEpoch.current++
    pending.current = null
    acknowledgedRevision.current = null
    inFlight.current = null
  }
  const [hostGeneration, setHostGeneration] = useState<number | null>(null)
  const lastContact = useRef(0)
  const lifecycle = useRef(0)
  const latestSample = useRef(-Infinity)
  const account = useRef(uid)
  account.current = uid
  const enabled = import.meta.env.VITE_ENABLE_SESSIONS === 'true'
  const key = uid ? `blinds-host-credential:${uid}` : null
  const [switchRequest, setSwitchRequest] = useState<{schedule: Schedule; onStarted: () => void} | null>(null)

  const accept = (contact: SessionContact, sentAt: number) => {
    if (sentAt >= latestSample.current) {
      const before = serverNow()
      const sample = anchorServerTime(contact.serverTimeMs, sentAt, performance.now())
      if (latestSample.current !== -Infinity) sample.serverAtReceiptMs = Math.max(sample.serverAtReceiptMs, before)
      else if (!contact.session && stateRef.current.timerPlaying && stateRef.current.timerDeadlineMs) {
        const payload = {timerDeadlineMs: stateRef.current.timerDeadlineMs + sample.serverAtReceiptMs - before}
        stateRef.current = {...stateRef.current, ...payload}
        dispatch({type: UPDATE_TIMER, payload, publish: false})
      }
      clock.current = sample
      latestSample.current = sentAt
    }
    setHostGeneration(contact.hostGeneration ?? null)
    lastContact.current = serverNow()
    if (!sessionRef.current || !contact.session || contact.session.sessionId !== sessionRef.current.sessionId || contact.session.revision >= sessionRef.current.revision) {
      sessionRef.current = contact.session
      setSession(contact.session)
    }
    const availabilityOffsetMs = Math.max(0, serverNow() - contact.serverTimeMs)
    setAvailableAt(contact.hostAvailableAtMs === null ? null : contact.hostAvailableAtMs + availabilityOffsetMs)
    updateView({resumeAvailable: contact.session !== null && contact.hostAvailableAtMs !== null && contact.serverTimeMs >= contact.hostAvailableAtMs})
    setNow(serverNow())
  }
  const remember = () => {
    if (key && host.current) sessionStorage.setItem(key, JSON.stringify(host.current))
  }
  const demote = () => {
    resetHostWork()
    host.current = null
    if (key) sessionStorage.removeItem(key)
    releaseLock.current?.()
    releaseLock.current = null
    changeStatus('View only')
    setSyncStatus('Unsynced')
  }
  const apply = (schedule: Schedule, timer: TimerSnapshot, publish: boolean) => {
    const payload = stateFromSnapshot(schedule, timer, serverNow())
    stateRef.current = {...stateRef.current, ...payload, timerRevision: (stateRef.current.timerRevision ?? 0) + (publish ? 1 : 0)}
    dispatch({type: UPDATE_TIMER, payload, publish})
  }
  const canControl = status === 'Standalone' || status === 'Hosting'

  const drain = async () => {
    if (!service || !host.current || inFlight.current !== null || !pending.current || statusRef.current !== 'Hosting') return
    const publicationEpoch = lifecycle.current
    const workEpoch = hostWorkEpoch.current
    const currentWork = () => lifecycle.current === publicationEpoch && hostWorkEpoch.current === workEpoch && statusRef.current === 'Hosting'
    inFlight.current = workEpoch
    try {
      const renewSent = performance.now()
      const renewed = await service.renewSessionHost(host.current.credential)
      if (!currentWork()) return
      accept(renewed, renewSent)
      while (pending.current && host.current && statusRef.current === 'Hosting') {
        const work: PendingPublication = pending.current
        const sentAt = performance.now()
        const result = await service.publishSessionState(work.request)
        if (!currentWork()) return
        acknowledgedRevision.current = work.revision
        accept(result, sentAt)
        if (pending.current === work) pending.current = null
      }
      if (!pending.current) {setSyncStatus('Synced'); setError(null)}
    } catch (failure) {
      if (!currentWork()) return
      setSyncStatus('Unsynced')
      if (!isRetryableSessionError(failure)) {
        demote(); void refreshView()
      }
    } finally { if (inFlight.current === workEpoch) inFlight.current = null }
  }
  const refreshView = async () => {
    if (!service || statusRef.current !== 'View only') return
    const epoch = lifecycle.current
    const workEpoch = hostWorkEpoch.current
    if (viewRefreshInFlight.current === workEpoch) return
    viewRefreshInFlight.current = workEpoch
    const currentView = () => epoch === lifecycle.current && workEpoch === hostWorkEpoch.current && statusRef.current === 'View only'
    const sentAt = performance.now()
    try {
      const contact = await service.getActiveSession()
      if (!currentView()) return
      accept(contact, sentAt)
      viewerRetryAt.current = 0
      setSyncStatus('Synced')
      if (sessionRef.current) apply(sessionRef.current.schedule, restoreTimer(sessionRef.current.timer, serverNow()), false)
      else changeStatus('Standalone')
    } catch {
      if (currentView()) {
        viewerRetryAt.current = performance.now() + refreshInterval
        setSyncStatus('Unsynced')
      }
    } finally {
      if (viewRefreshInFlight.current === workEpoch) {
        viewRefreshInFlight.current = null
        if (currentView()) nextViewerRead.current = performance.now() + refreshInterval
      }
    }
  }
  const refreshViewRef = useRef(refreshView)
  refreshViewRef.current = refreshView
  useEffect(() => {
    nextViewerRead.current = status === 'View only' ? performance.now() + refreshInterval : Infinity
  }, [status, uid, service, refreshInterval])
  const drainRef = useRef(drain)
  drainRef.current = drain
  const queue = (schedule?: Schedule, timer?: TimerSnapshot) => {
    if (!host.current || statusRef.current !== 'Hosting') return
    host.current.sequence++
    remember()
    pending.current = {revision: stateRef.current.timerRevision ?? 0, request: {
      credential: host.current.credential, sequence: host.current.sequence,
      eventId: crypto.randomUUID(), timer: timer ?? snapshotFromState(stateRef.current, serverNow()),
      schedule: schedule ?? scheduleFromState(stateRef.current),
    }}
    setSyncStatus('Pending')
    void drainRef.current()
  }
  const queueRef = useRef(queue)
  queueRef.current = queue

  const recover = async () => {
    if (!enabled || !service || !uid) return
    const recoveryUid = uid
    const recoveryEpoch = lifecycle.current
    if (recoveryInFlight.current === recoveryEpoch) return
    recoveryInFlight.current = recoveryEpoch
    changeStatus('Recovering')
    setError(null)
    try {
      const sentAt = performance.now()
      const result = await service.getActiveSession()
      if (account.current !== recoveryUid || lifecycle.current !== recoveryEpoch) return
      accept(result, sentAt)
      if (result.legacySessionIds?.length) {changeStatus('Legacy'); return}
      let saved: SavedHost | null = null
      try { saved = key ? JSON.parse(sessionStorage.getItem(key) ?? 'null') : null } catch { saved = null }
      if (!result.session) {
        if (key) sessionStorage.removeItem(key)
        changeStatus('Standalone')
        return
      }
      if (saved?.credential?.sessionId === result.session.sessionId && !releaseLock.current) {
        const release = await acquireTabOwnership(uid, `${saved.credential.sessionId}:${saved.credential.generation}`)
        if (account.current !== recoveryUid || lifecycle.current !== recoveryEpoch) {release?.(); return}
        releaseLock.current = release
      }
      if (!releaseLock.current || !saved?.credential || saved.credential.sessionId !== result.session.sessionId) {
        changeStatus('View only')
        apply(result.session.schedule, restoreTimer(result.session.timer, serverNow()), false)
        return
      }
      let restorationSource = result.session
      try {
        const renewedAt = performance.now()
        const renewed = await service.renewSessionHost(saved.credential)
        if (account.current !== recoveryUid || lifecycle.current !== recoveryEpoch) return
        accept(renewed, renewedAt)
        restorationSource = renewed.session ?? restorationSource
      } catch (failure) {
        if (account.current !== recoveryUid || lifecycle.current !== recoveryEpoch) return
        if (isRetryableSessionError(failure)) throw failure
        demote()
        apply(result.session.schedule, restoreTimer(result.session.timer, serverNow()), false)
        return
      }
      resetHostWork()
      host.current = {credential: saved.credential, sequence: saved.sequence}
      const restored = restoreTimer(restorationSource.timer, serverNow())
      apply(restorationSource.schedule, restored, false)
      changeStatus('Hosting')
      setSyncStatus(projectTimer(restorationSource.timer, serverNow()).missedBoundary ? 'Unsynced' : 'Synced')
      queueRef.current(restorationSource.schedule, restored)
    } catch {
      if (account.current !== recoveryUid || lifecycle.current !== recoveryEpoch) return
      changeStatus('Recovering')
      setError('Recovery is waiting for a connection.')
      setSyncStatus('Unsynced')
    } finally {
      if (recoveryInFlight.current === recoveryEpoch) recoveryInFlight.current = null
    }
  }
  const recoverRef = useRef(recover)
  recoverRef.current = recover

  useEffect(() => {
    lifecycle.current++
    setNow(serverNow())
    setBusy(false)
    setError(null)
    setSyncStatus('Synced')
    updateView({resumeAvailable: false, availableAtMs: null})
    setSwitchRequest(null)
    resetHostWork()
    recoveryInFlight.current = null
    host.current = null
    sessionRef.current = null
    setSession(null)
    releaseLock.current?.()
    releaseLock.current = null
    if (enabled && service && uid) void recoverRef.current()
    else changeStatus('Standalone')
    return () => {
      lifecycle.current++
      resetHostWork()
      releaseLock.current?.()
      releaseLock.current = null
    }
  }, [uid, service, enabled])

  useEffect(() => {
    if (statusRef.current === 'Hosting') queueRef.current()
  }, [state.timerRevision])

  const tickRef = useRef<() => void>(() => {})
  tickRef.current = () => {
    const currentNow = serverNow()
    setNow(currentNow)
    const local = stateRef.current
    if ((statusRef.current === 'Standalone' || statusRef.current === 'Hosting') && local.timerPlaying) {
      const before = snapshotFromState(local, currentNow)
      const reconciled = reconcileLiveTimer(scheduleFromState(local), before, currentNow)
      const transitioned = reconciled.levelIndex !== before.levelIndex || reconciled.breakIndex !== before.breakIndex || reconciled.onBreak !== before.onBreak || reconciled.clock.kind === 'suddenDeath'
      apply(scheduleFromState(local), reconciled, transitioned)
      if (transitioned) playAlert()
    }
    if (statusRef.current === 'Hosting' && host.current && currentNow - lastContact.current >= HOST_HEARTBEAT_MS && inFlight.current === null) {
      lastContact.current = currentNow
      const sentAt = performance.now()
      const heartbeatEpoch = lifecycle.current
      const heartbeatWorkEpoch = hostWorkEpoch.current
      void service?.renewSessionHost(host.current.credential).then(result => {
        if (heartbeatEpoch === lifecycle.current && heartbeatWorkEpoch === hostWorkEpoch.current && statusRef.current === 'Hosting') accept(result, sentAt)
      }).catch(failure => {
        if (heartbeatEpoch !== lifecycle.current || heartbeatWorkEpoch !== hostWorkEpoch.current || statusRef.current !== 'Hosting') return
        setSyncStatus('Unsynced')
        if (!isRetryableSessionError(failure)) {demote(); void refreshView()}
        else if (!pending.current) queueRef.current()
      })
    }
    if (statusRef.current === 'View only' && sessionRef.current) {
      apply(sessionRef.current.schedule, restoreTimer(sessionRef.current.timer, currentNow), false)
      const eligibilityDue = !view.resumeAvailable && availableAtMs !== null && currentNow >= availableAtMs && performance.now() >= viewerRetryAt.current
      if (performance.now() >= nextViewerRead.current || eligibilityDue) void refreshViewRef.current()
    }
    if (pending.current) void drainRef.current()
  }
  useEffect(() => {
    let cancelled = false
    let timeout: ReturnType<typeof setTimeout>
    const next = () => {
      if (cancelled) return
      tickRef.current()
      timeout = setTimeout(next, 1000)
    }
    timeout = setTimeout(next, 1000)
    const returning = () => {
      tickRef.current()
      if (statusRef.current === 'Hosting') queueRef.current()
      else if (statusRef.current === 'Recovering') void recoverRef.current()
      else if (statusRef.current === 'View only') void refreshViewRef.current()
    }
    window.addEventListener('online', returning)
    window.addEventListener('pageshow', returning)
    document.addEventListener('visibilitychange', returning)
    return () => {
      cancelled = true
      clearTimeout(timeout)
      window.removeEventListener('online', returning)
      window.removeEventListener('pageshow', returning)
      document.removeEventListener('visibilitychange', returning)
    }
  }, [])

  const command = (operation: 'toggle' | 'reset' | 'skip', segment?: TimerSegment) => {
    if (!['Standalone', 'Hosting'].includes(statusRef.current)) return
    const local = stateRef.current
    const schedule = scheduleFromState(local)
    let timer = reconcileLiveTimer(schedule, snapshotFromState(local, serverNow()), serverNow())
    if (segment && (segment.levelIndex !== timer.levelIndex || segment.breakIndex !== timer.breakIndex || segment.onBreak !== timer.onBreak)) {
      apply(schedule, timer, true)
      playAlert()
      return
    }
    if (timer.clock.kind === 'suddenDeath') return
    if (operation === 'skip') timer = skipSnapshot(schedule, timer, serverNow())
    else if (operation === 'reset') {
      const remainingMs = (timer.onBreak ? schedule.breaks[timer.breakIndex].length : schedule.levelLength) * 60000
      timer = {...timer, clock: timer.clock.kind === 'running' ? {kind: 'running', deadlineMs: serverNow() + remainingMs} : {kind: 'paused', remainingMs}}
    } else {
      const remainingMs = projectTimer(timer, serverNow()).remainingMs ?? 0
      const startingZero = timer.clock.kind === 'paused' && remainingMs === 0
      timer = {...timer, hasStarted: true, clock: timer.clock.kind === 'running' ? {kind: 'paused', remainingMs} : {kind: 'running', deadlineMs: serverNow() + remainingMs}}
      if (startingZero) {
        timer = reconcileLiveTimer(schedule, timer, serverNow())
        playAlert()
      }
    }
    apply(schedule, {...timer, capturedAtMs: serverNow()}, true)
  }
  const perform = async (action: () => Promise<void>) => {
    const actionEpoch = lifecycle.current
    setBusy(true); setError(null)
    try {await action()} catch (failure) {
      if (actionEpoch !== lifecycle.current) return
      if (statusRef.current !== 'Hosting') {releaseLock.current?.(); releaseLock.current = null}
      if (statusRef.current === 'Standalone' && sessionRef.current) changeStatus('View only')
      setError(`Unable to update session. ${failure instanceof Error ? failure.message : 'Please retry.'}`)
    } finally {if (actionEpoch === lifecycle.current) setBusy(false)}
  }
  const ensureLock = async (credential?: HostCredential, expectedEpoch = lifecycle.current) => {
    if (!uid) throw new Error('Sign in to manage sessions.')
    if (expectedEpoch !== lifecycle.current) throw new Error('Session action was cancelled.')
    if (credential) {releaseLock.current?.(); releaseLock.current = null}
    if (!releaseLock.current) {
      const release = await acquireTabOwnership(uid, credential ? `${credential.sessionId}:${credential.generation}` : 'claim')
      if (expectedEpoch !== lifecycle.current) {release?.(); throw new Error('Session action was cancelled.')}
      releaseLock.current = release
    }
    if (!releaseLock.current) throw new Error('Another tab may be hosting. This tab is view-only.')
  }
  const publicationPending = canControl && session !== null && acknowledgedRevision.current !== (state.timerRevision ?? 0)
  const effectiveSyncStatus = session && projectTimer(session.timer, now).missedBoundary ? 'Unsynced'
    : syncStatus === 'Synced' && publicationPending ? 'Pending' : syncStatus
  const controls: SessionControls = {
    session, status, hostGeneration, syncStatus: effectiveSyncStatus,
    serverNowMs: now, availableAtMs, error, busy, canControl, canResume: status === 'View only' && view.resumeAvailable,
    toggle: () => command('toggle'), reset: segment => command('reset', segment), skip: segment => command('skip', segment),
    startSchedule: (schedule, onStarted) => {
      if (!canControl) return
      const startEpoch = lifecycle.current
      const start = () => {
        if (startEpoch !== lifecycle.current) return
        if (!['Standalone', 'Hosting'].includes(statusRef.current)) return
        const timer: TimerSnapshot = {hasStarted: false, levelIndex: 0, breakIndex: 0, onBreak: false, capturedAtMs: serverNow(), clock: schedule.blinds.length === 1 ? {kind: 'suddenDeath'} : {kind: 'paused', remainingMs: schedule.levelLength * 60000}}
        apply(schedule, timer, true)
        onStarted()
      }
      if (status === 'Hosting' && schedule.name !== stateRef.current.name) setSwitchRequest({schedule, onStarted: start})
      else start()
    },
    create: () => perform(async () => {
      if (!service || statusRef.current !== 'Standalone') return
      const actionEpoch = lifecycle.current
      await ensureLock(undefined, actionEpoch)
      const schedule = scheduleFromState(stateRef.current)
      const timer = reconcileLiveTimer(schedule, snapshotFromState(stateRef.current, serverNow()), serverNow())
      apply(schedule, timer, false)
      const revision = stateRef.current.timerRevision ?? 0
      const sentAt = performance.now()
      const result = await service.createHostedSession(schedule, timer)
      if (actionEpoch !== lifecycle.current) return
      accept(result, sentAt)
      await ensureLock(result.credential, actionEpoch)
      resetHostWork()
      host.current = {credential: result.credential, sequence: 0}
      remember()
      acknowledgedRevision.current = revision
      changeStatus('Hosting')
      setSyncStatus('Synced')
      if (revision !== (stateRef.current.timerRevision ?? 0)) queueRef.current()
    }),
    remove: () => perform(async () => {
      if (!service || !host.current || statusRef.current !== 'Hosting') return
      const actionEpoch = lifecycle.current
      await service.deleteHostedSession(host.current.credential)
      if (actionEpoch !== lifecycle.current) return
      resetHostWork()
      host.current = null; sessionRef.current = null; setSession(null)
      if (key) sessionStorage.removeItem(key)
      releaseLock.current?.(); releaseLock.current = null
      changeStatus('Standalone'); setSyncStatus('Synced')
    }),
    resume: (expectedGeneration) => perform(async () => {
      if (!service || !sessionRef.current || statusRef.current !== 'View only' || (expectedGeneration === undefined ? !view.resumeAvailable : expectedGeneration !== hostGeneration)) return
      const actionEpoch = lifecycle.current
      await ensureLock(undefined, actionEpoch)
      const sentAt = performance.now()
      const result = await service.resumeSessionHosting(sessionRef.current.sessionId, expectedGeneration)
      if (actionEpoch !== lifecycle.current) return
      accept(result, sentAt)
      if (!result.session) throw new Error('Session is unavailable.')
      await ensureLock(result.credential, actionEpoch)
      resetHostWork()
      host.current = {credential: result.credential, sequence: 0}
      remember()
      const restored = restoreTimer(result.session.timer, serverNow())
      apply(result.session.schedule, restored, false)
      changeStatus('Hosting')
      queueRef.current(result.session.schedule, restored)
    }),
    retire: () => perform(async () => {
      if (!service || statusRef.current !== 'Legacy') return
      const actionEpoch = lifecycle.current
      await service.retireLegacySessions()
      if (actionEpoch === lifecycle.current) await recoverRef.current()
    }),
    retry: async () => {
      if (statusRef.current === 'Recovering') await recoverRef.current()
      else if (statusRef.current === 'Hosting') await drainRef.current()
      else await refreshView()
    },
  }
  return <SessionContext.Provider value={controls}>
    {children}
    <Dialog open={!!switchRequest} onClose={() => setSwitchRequest(null)} aria-labelledby="switch-schedule-title">
      <DialogTitle id="switch-schedule-title">Start a different schedule?</DialogTitle>
      <DialogContent>The session will use {switchRequest?.schedule.name} from its first level, paused.</DialogContent>
      <DialogActions><Button onClick={() => setSwitchRequest(null)}>Cancel</Button><Button onClick={() => {switchRequest?.onStarted(); setSwitchRequest(null)}}>Start schedule</Button></DialogActions>
    </Dialog>
  </SessionContext.Provider>
}
